import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Play, Info } from "lucide-react";
import api from "@/lib/api";
import { useLoc } from "@/lib/loc";
import Rail from "@/components/Rail";
import Seo from "@/components/Seo";
import HeroPreview from "@/components/HeroPreview";
import { Loader2 } from "lucide-react";

export default function Home() {
  const { t } = useTranslation();
  const loc = useLoc();
  const [heroIdx, setHeroIdx] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ["home"],
    queryFn: async () => (await api.get("/home")).data,
  });

  const heroes = data?.hero || [];
  useEffect(() => {
    if (heroes.length < 2) return;
    const id = setInterval(() => setHeroIdx((i) => (i + 1) % heroes.length), 8000);
    return () => clearInterval(id);
  }, [heroes.length]);

  if (isLoading)
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );

  const hero = heroes[heroIdx];

  return (
    <div className="pb-10">
      <Seo title="Accueil" description={t("brand.tagline")} image={hero?.thumbnail_url} />

      {hero && (
        <section className="relative h-[82vh] min-h-[560px] w-full" data-testid="hero-banner">
          <img src={hero.thumbnail_url} alt={loc(hero.title)} className="absolute inset-0 h-full w-full object-cover" />
          <HeroPreview videoId={hero.id} poster={hero.thumbnail_url} />
          <div className="absolute inset-0 hero-fade-left" />
          <div className="absolute inset-x-0 bottom-0 h-2/3 hero-fade" />
          <div className="relative z-10 flex h-full max-w-[1600px] flex-col justify-end px-6 pb-24 md:px-12 md:pb-28">
            <div className="max-w-2xl animate-fadeup">
              {hero.is_sponsored && (
                <span className="mb-3 inline-block rounded-sm border border-white/10 bg-black/60 px-2 py-1 text-[10px] uppercase tracking-widest text-white/80 backdrop-blur-md">
                  {t("common.sponsored")}
                </span>
              )}
              <h1 className="font-heading text-4xl font-black uppercase leading-[0.95] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
                {loc(hero.title)}
              </h1>
              <p className="mt-4 line-clamp-2 max-w-xl text-base leading-relaxed text-white/70">
                {loc(hero.description)}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to={`/video/${hero.slug || hero.id}`}
                  data-testid="hero-play-btn"
                  className="flex items-center gap-2 rounded-full bg-primary px-7 py-3 font-semibold text-primary-foreground transition-colors hover:bg-[hsl(var(--primary))]/90"
                >
                  <Play className="h-5 w-5 fill-current" /> {t("common.play")}
                </Link>
                <Link
                  to={`/video/${hero.slug || hero.id}`}
                  data-testid="hero-info-btn"
                  className="flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-7 py-3 font-semibold text-foreground backdrop-blur-md transition-colors hover:bg-white/20"
                >
                  <Info className="h-5 w-5" /> {t("common.moreInfo")}
                </Link>
              </div>
            </div>
          </div>
          {heroes.length > 1 && (
            <div className="absolute bottom-6 right-6 z-10 flex gap-1.5 md:right-12">
              {heroes.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setHeroIdx(i)}
                  className={`h-1.5 rounded-full transition-all ${i === heroIdx ? "w-6 bg-primary" : "w-1.5 bg-white/40"}`}
                  aria-label={`hero ${i}`}
                />
              ))}
            </div>
          )}
        </section>
      )}

      <div className="relative z-20 -mt-16">
        <Rail title={t("common.featured")} videos={data?.featured} testId="rail-featured" />
        <Rail title={t("common.trending")} videos={data?.trending} testId="rail-trending" />
        {data?.rails?.map((r) => (
          <Rail key={r.category.id} title={loc(r.category.name)} videos={r.videos} testId={`rail-cat-${r.category.id}`} />
        ))}
      </div>
    </div>
  );
}
